import React, { Suspense } from "react";
import { Outlet, useParams } from "react-router";
import { toNumber } from "lodash";
import { useTranslation } from "react-i18next";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import { RouteLoader } from "@/shared/ui/RouteLoader";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel } from "@/shared/constants";
import { ResourceEnum } from "@/shared/constants/resources";

const resourceLoadingKey: Record<string, string> = {
  [ResourceEnum.project]: "common.loading_project",
  [ResourceEnum.team]: "common.loading_team",
};

interface RequireResourceAccessProps {
  resourceType: ResourceEnum.team | ResourceEnum.project;
  accessLevel: AccessLevel;
  paramKey: "teamId" | "projectId";
  className?: string;
}

export function RequireResourceAccess({
  resourceType,
  accessLevel,
  paramKey,
  className = "overflow-y-auto",
}: RequireResourceAccessProps) {
  const params = useParams();
  const resourceId = toNumber(params[paramKey]);

  const { t } = useTranslation();
  const { validateAccessToResource, permissionsLoading } =
    useViewerPermissions();

  const hasAccess =
    !!resourceId &&
    validateAccessToResource({
      resourceType,
      resourceId,
      accessLevel,
    });

  if (permissionsLoading)
    return (
      <ScreenContainer
        loading
        loadingMessage={t("common.checking_access")}
        className={className}
      />
    );
  if (!hasAccess)
    return <ScreenContainer permissionLocked className={className} />;

  return (
    <Suspense
      fallback={
        <RouteLoader
          loadingMessage={t(
            resourceLoadingKey[resourceType] ?? "common.loading_content"
          )}
        />
      }
    >
      <Outlet />
    </Suspense>
  );
}
